import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowDown,
  ArrowUp,
  CornerDownLeft,
  FileText,
  PackageSearch,
  Plus,
  Search,
  Users,
  X,
} from "lucide-react";

import {
  fetchCustomers,
  fetchOrders,
  fetchProducts,
  enrichOrdersWithCustomers,
  getOrders,
  type ClientRecord,
  type OrderRecord,
  type ProductRecord,
} from "@/lib/utils";
import { getAuthenticatedUser } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

// ----------------------------------------------------------------------------

interface GlobalCommandPaletteProps {
  className?: string;
}

export interface PaletteItem {
  id: string;
  category: "action" | "order" | "client" | "product";
  categoryLabel: string;
  title: string;
  subtitle: string;
  badge?: string;
  score: number;
  imageUrl?: string;
  icon?: typeof Search;
  onSelect: () => void;
}

function calculateScore(query: string, text: string, weight: number): number {
  if (!query || !text) return 0;
  const q = query.toLowerCase();
  const t = text.toLowerCase();

  if (t === q) return 100 * weight;
  if (t.startsWith(q)) return 60 * weight;
  if (t.includes(q)) return 20 * weight;
  return 0;
}

export function GlobalCommandPalette(props: GlobalCommandPaletteProps) {
  // -------------------------------------
  // Props destructuring
  // -------------------------------------

  const { className } = props;

  // -------------------------------------
  // Hooks (e.g. useState, useMemo ...)
  // -------------------------------------

  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const [orders, setOrders] = useState<OrderRecord[]>([]);
  const [clients, setClients] = useState<ClientRecord[]>([]);
  const [products, setProducts] = useState<ProductRecord[]>([]);
  const [isLoadingBaseData, setIsLoadingBaseData] = useState(false);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [baseWarning, setBaseWarning] = useState<string | null>(null);
  const [productsWarning, setProductsWarning] = useState<string | null>(null);
  const [hasLoadedBaseData, setHasLoadedBaseData] = useState(false);

  const navigate = useNavigate();
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const normalizedQuery = searchQuery.trim().toLowerCase();
  const hasQuery = normalizedQuery.length > 0;

  // -------------------------------------
  // Quick Actions (shown when query is empty)
  // -------------------------------------

  const quickActions = useMemo<PaletteItem[]>(
    () => [
      {
        id: "action-new-order",
        category: "action",
        categoryLabel: "Azioni Rapide",
        title: "Nuovo ordine",
        subtitle: "Apri il flusso di creazione ordine B2B",
        icon: Plus,
        score: 100,
        onSelect: () => {
          setIsOpen(false);
          navigate("/nuovo-ordine");
        },
      },
      {
        id: "action-orders",
        category: "action",
        categoryLabel: "Azioni Rapide",
        title: "Ordini",
        subtitle: "Vai alla lista ed alla gestione ordini",
        icon: FileText,
        score: 90,
        onSelect: () => {
          setIsOpen(false);
          navigate("/ordini");
        },
      },
      {
        id: "action-clients",
        category: "action",
        categoryLabel: "Azioni Rapide",
        title: "Clienti",
        subtitle: "Vai all'anagrafica clienti",
        icon: Users,
        score: 80,
        onSelect: () => {
          setIsOpen(false);
          navigate("/clienti");
        },
      },
      {
        id: "action-products",
        category: "action",
        categoryLabel: "Azioni Rapide",
        title: "Prodotti",
        subtitle: "Apri il catalogo prodotti B2B",
        icon: PackageSearch,
        score: 70,
        onSelect: () => {
          setIsOpen(false);
          navigate("/prodotti");
        },
      },
    ],
    [navigate],
  );

  const currentUser = useMemo(() => getAuthenticatedUser(), []);
  const canViewAll = currentUser?.canViewAll || currentUser?.id?.toLowerCase() === "dev-nogravity";
  const agentTag = currentUser?.agentTag;
  const agentCode = currentUser?.agentCode;

  const agentScopedClients = useMemo(() => {
    if (canViewAll) return clients.filter((c) => !c.isAgent);
    if (!agentTag && !agentCode) return [];
    const tagLower = agentTag ? agentTag.toLowerCase().trim() : "";
    const codeStr = agentCode ? agentCode.trim() : "";

    return clients.filter((c) => {
      if (c.isAgent === true) return false;
      if (
        currentUser?.id &&
        c.id.toLowerCase() === currentUser.id.toLowerCase()
      )
        return false;
      if (
        currentUser?.email &&
        c.email.toLowerCase() === currentUser.email.toLowerCase()
      )
        return false;
      const tags = (c.tags ?? []).map((t) => t.toLowerCase().trim());
      return tags.some((t) => {
        if (tagLower && t === tagLower) return true;
        if (codeStr && (t === `agente-${codeStr}` || t === `agent-${codeStr}`))
          return true;
        return false;
      });
    });
  }, [clients, canViewAll, agentTag, agentCode, currentUser]);

  const agentScopedOrders = useMemo(() => {
    if (canViewAll) return orders;
    if (!agentTag && !agentCode) return [];
    const ids = new Set(agentScopedClients.map((c) => c.id.toLowerCase()));
    const emails = new Set(
      agentScopedClients.map((c) => c.email.toLowerCase()),
    );
    const names = new Set(agentScopedClients.map((c) => c.name.toLowerCase()));
    if (currentUser?.fullName) names.add(currentUser.fullName.toLowerCase());

    return orders.filter((o) => {
      const matchId = o.clientId && ids.has(o.clientId.toLowerCase());
      const matchEmail = o.email && emails.has(o.email.toLowerCase());
      const matchName =
        o.customerName && names.has(o.customerName.toLowerCase());
      return matchId || matchEmail || matchName;
    });
  }, [orders, agentScopedClients, canViewAll, agentTag, agentCode, currentUser]);

  // -------------------------------------
  // Search Results with Smart Ranking
  // -------------------------------------

  const orderItems = useMemo<PaletteItem[]>(() => {
    if (!hasQuery) return [];

    return agentScopedOrders
      .map((order): PaletteItem | null => {
        const idScore = calculateScore(normalizedQuery, order.id, 2.5);
        const nameScore = calculateScore(
          normalizedQuery,
          order.customerName || "",
          1.8,
        );
        const emailScore = calculateScore(
          normalizedQuery,
          order.email || "",
          1.2,
        );
        const maxScore = Math.max(idScore, nameScore, emailScore);

        if (maxScore === 0) return null;

        return {
          id: `order-${order.id}`,
          category: "order" as const,
          categoryLabel: "Ordini",
          title: `Ordine #${order.id}`,
          subtitle: `${order.customerName || "Cliente sconosciuto"} · ${order.email || ""}`,
          badge: order.total,
          score: maxScore,
          icon: FileText,
          onSelect: () => {
            setIsOpen(false);
            navigate(`/ordini/${order.id}`);
          },
        };
      })
      .filter((item): item is PaletteItem => item !== null);
  }, [hasQuery, normalizedQuery, agentScopedOrders, navigate]);

  const clientItems = useMemo<PaletteItem[]>(() => {
    if (!hasQuery) return [];

    return agentScopedClients
      .map((client): PaletteItem | null => {
        const nameScore = calculateScore(normalizedQuery, client.name, 2.0);
        const emailScore = calculateScore(normalizedQuery, client.email, 1.5);
        const maxScore = Math.max(nameScore, emailScore);

        if (maxScore === 0) return null;

        return {
          id: `client-${client.id}`,
          category: "client" as const,
          categoryLabel: "Clienti",
          title: client.name || "Cliente",
          subtitle: client.email || "Nessuna email disponibile",
          badge: client.city ? `${client.city} (${client.province})` : undefined,
          score: maxScore,
          icon: Users,
          onSelect: () => {
            setIsOpen(false);
            navigate(`/clienti/${client.id}`);
          },
        };
      })
      .filter((item): item is PaletteItem => item !== null);
  }, [agentScopedClients, hasQuery, normalizedQuery, navigate]);

  const productItems = useMemo<PaletteItem[]>(() => {
    if (!hasQuery) return [];

    return products
      .map((product): PaletteItem | null => {
        const codeScore = calculateScore(normalizedQuery, product.code, 2.5);
        const skuScore = calculateScore(normalizedQuery, product.sku || "", 2.0);
        const nameScore = calculateScore(normalizedQuery, product.name || "", 1.5);
        const descScore = calculateScore(
          normalizedQuery,
          product.description || "",
          0.8,
        );
        const maxScore = Math.max(codeScore, skuScore, nameScore, descScore);

        if (maxScore === 0) return null;

        const searchValue = product.sku || product.code;

        return {
          id: `product-${product.code}`,
          category: "product" as const,
          categoryLabel: "Prodotti",
          title: product.name || product.code,
          subtitle: `SKU ${product.sku || product.code} · ${product.code}`,
          badge: product.price,
          score: maxScore,
          imageUrl: product.imageUrl,
          icon: PackageSearch,
          onSelect: () => {
            setIsOpen(false);
            navigate(`/prodotti?search=${encodeURIComponent(searchValue)}`);
          },
        };
      })
      .filter((item): item is PaletteItem => item !== null);
  }, [hasQuery, normalizedQuery, products, navigate]);

  // -------------------------------------
  // Combined and Sorted Results
  // -------------------------------------

  const flatItems = useMemo<PaletteItem[]>(() => {
    if (!hasQuery) {
      return quickActions;
    }

    const allResults = [...orderItems, ...clientItems, ...productItems];
    return allResults.sort((a, b) => b.score - a.score).slice(0, 15);
  }, [hasQuery, quickActions, orderItems, clientItems, productItems]);

  // Grouped items by category for visual rendering
  const groupedItems = useMemo(() => {
    const categories: { label: string; items: { item: PaletteItem; globalIndex: number }[] }[] = [];

    flatItems.forEach((item, globalIndex) => {
      let group = categories.find((c) => c.label === item.categoryLabel);
      if (!group) {
        group = { label: item.categoryLabel, items: [] };
        categories.push(group);
      }
      group.items.push({ item, globalIndex });
    });

    return categories;
  }, [flatItems]);

  // -------------------------------------
  // Effects
  // -------------------------------------

  // Global ⌘K / Ctrl+K shortcut listener
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setIsOpen((prev) => !prev);
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Reset active index when query or items list changes
  useEffect(() => {
    setActiveIndex(0);
  }, [searchQuery, flatItems.length]);

  // Smooth scroll active element into view when navigating with keyboard
  useEffect(() => {
    if (!isOpen) return;
    const currentEl = itemRefs.current[activeIndex];
    if (currentEl) {
      currentEl.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [activeIndex, isOpen]);

  // Load base customers and orders on open
  useEffect(() => {
    if (!isOpen || hasLoadedBaseData) return;

    let isMounted = true;

    async function loadBaseData(): Promise<void> {
      setIsLoadingBaseData(true);
      setBaseWarning(null);

      const currentUser = getAuthenticatedUser();
      const canViewAll = Boolean(
        currentUser?.canViewAll ||
        currentUser?.id?.toLowerCase() === "dev-nogravity"
      );
      const activeAgent = canViewAll
        ? ""
        : (currentUser?.agentTag || (currentUser?.agentCode ? `agente-${currentUser.agentCode}` : "none"));

      const warnings: string[] = [];
      const [customersResult, ordersResult] = await Promise.allSettled([
        fetchCustomers(activeAgent),
        fetchOrders(undefined, activeAgent),
      ]);

      if (!isMounted) return;

      let resolvedClients: ClientRecord[] = [];
      if (customersResult.status === "fulfilled") {
        resolvedClients = customersResult.value;
        setClients(resolvedClients);
      } else {
        setClients([]);
        warnings.push("clienti live non disponibili");
      }

      if (ordersResult.status === "fulfilled") {
        const apiOrders = ordersResult.value;
        const localOrders = getOrders();
        const apiOrderIds = new Set(apiOrders.map((o) => o.id));
        const localOnlyOrders = localOrders.filter((o) => !apiOrderIds.has(o.id));
        const rawMerged = [...localOnlyOrders, ...apiOrders];
        const merged = enrichOrdersWithCustomers(rawMerged, resolvedClients);
        setOrders(merged);
      } else {
        setOrders(getOrders());
        warnings.push("ordini live non disponibili");
      }

      setBaseWarning(
        warnings.length > 0
          ? `Ricerca parziale: ${warnings.join(" · ")}.`
          : null,
      );
      setHasLoadedBaseData(true);
      setIsLoadingBaseData(false);
    }

    void loadBaseData();

    return () => {
      isMounted = false;
    };
  }, [hasLoadedBaseData, isOpen]);

  // Load product search results dynamically
  useEffect(() => {
    if (!isOpen || !hasQuery) {
      setProducts([]);
      setProductsWarning(null);
      setIsLoadingProducts(false);
      return;
    }

    let isMounted = true;
    const timeoutId = window.setTimeout(() => {
      void (async () => {
        setIsLoadingProducts(true);
        setProductsWarning(null);

        try {
          const response = await fetchProducts(searchQuery.trim(), 1);

          if (!isMounted) return;
          setProducts(response.products.slice(0, 10));
        } catch (error) {
          console.error("[GlobalCommandPalette] Product search failed:", error);
          if (!isMounted) return;
          setProducts([]);
          setProductsWarning(
            "Prodotti live non disponibili nella ricerca rapida.",
          );
        } finally {
          if (isMounted) {
            setIsLoadingProducts(false);
          }
        }
      })();
    }, 200);

    return () => {
      isMounted = false;
      window.clearTimeout(timeoutId);
    };
  }, [isOpen, hasQuery, searchQuery]);

  // Reset search state on modal close
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery("");
      setActiveIndex(0);
      setProducts([]);
      setProductsWarning(null);
    }
  }, [isOpen]);

  // -------------------------------------
  // Keyboard Handler for Input (Spotlight Style)
  // -------------------------------------

  function handleInputKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (flatItems.length > 0) {
        setActiveIndex((prev) => (prev + 1) % flatItems.length);
      }
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      if (flatItems.length > 0) {
        setActiveIndex((prev) => (prev - 1 + flatItems.length) % flatItems.length);
      }
    } else if (event.key === "Enter") {
      event.preventDefault();
      const activeItem = flatItems[activeIndex];
      if (activeItem) {
        activeItem.onSelect();
      }
    } else if (event.key === "Escape") {
      event.preventDefault();
      setIsOpen(false);
    }
  }

  // -------------------------------------
  // Component local variables
  // -------------------------------------

  const hasSearchResults = flatItems.length > 0;

  return (
    <>
      <Button
        variant="outline"
        className={`h-11 w-full justify-between border-gray-200 bg-white text-left text-gray-500 hover:bg-gray-50 shadow-xs ${className || ""}`}
        onClick={() => setIsOpen(true)}
      >
        <span className="flex items-center gap-3">
          <Search className="h-4 w-4 text-gray-400" />
          <span className="font-normal">Cerca ordini, clienti, prodotti...</span>
        </span>
        <kbd className="hidden rounded-md border border-gray-200 bg-gray-50 px-2 py-0.5 text-[11px] font-semibold text-gray-500 sm:inline-flex items-center gap-0.5">
          ⌘K
        </kbd>
      </Button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent
          className="max-w-2xl p-0 overflow-hidden rounded-2xl border-gray-200 shadow-2xl bg-white gap-0"
          showCloseButton={false}
        >
          <DialogHeader className="sr-only">
            <DialogTitle>Ricerca Globale Spotlight</DialogTitle>
          </DialogHeader>

          {/* Search Input Box */}
          <div className="relative flex items-center border-b border-gray-100 px-4 py-3.5 bg-gray-50/50">
            <Search className="h-5 w-5 shrink-0 text-gray-400 ml-1 mr-3" />
            <Input
              autoFocus
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleInputKeyDown}
              placeholder="Digita per cercare ordini, clienti o prodotti..."
              className="h-10 border-0 bg-transparent text-base text-gray-900 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 px-0 placeholder:text-gray-400"
            />
            {hasQuery && (
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-gray-400 hover:text-gray-600 rounded-full"
                onClick={() => setSearchQuery("")}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>

          {/* Warnings & Messages */}
          <div className="max-h-[60vh] overflow-y-auto p-4 space-y-4">
            {baseWarning && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-2.5 text-xs text-amber-900">
                {baseWarning}
              </div>
            )}

            {productsWarning && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-2.5 text-xs text-amber-900">
                {productsWarning}
              </div>
            )}

            {(isLoadingBaseData || isLoadingProducts) && (
              <div className="flex items-center gap-2 py-2 text-xs font-medium text-blue-600">
                <Search className="h-3.5 w-3.5 animate-spin" />
                <span>Ricerca in corso nei dati B2B...</span>
              </div>
            )}

            {/* Results grouped by category */}
            {groupedItems.map((group) => (
              <div key={group.label} className="space-y-1.5">
                <div className="px-2 text-[11px] font-bold uppercase tracking-wider text-gray-400">
                  {group.label}
                </div>

                <div className="space-y-1">
                  {group.items.map(({ item, globalIndex }) => {
                    const isActive = globalIndex === activeIndex;
                    const Icon = item.icon || Search;

                    return (
                      <button
                        key={item.id}
                        ref={(el) => {
                          itemRefs.current[globalIndex] = el;
                        }}
                        type="button"
                        className={`flex w-full items-center justify-between gap-3 rounded-xl px-3.5 py-2.5 text-left transition-all duration-150 cursor-pointer ${
                          isActive
                            ? "bg-[#0D6EFD] text-white shadow-sm ring-2 ring-blue-400/30"
                            : "text-gray-900 hover:bg-gray-100/80"
                        }`}
                        onMouseEnter={() => setActiveIndex(globalIndex)}
                        onClick={item.onSelect}
                      >
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <div
                            className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg overflow-hidden ${
                              isActive
                                ? "bg-white/20 text-white"
                                : "bg-gray-100 text-gray-600"
                            }`}
                          >
                            {item.imageUrl ? (
                              <img
                                src={item.imageUrl}
                                alt={item.title}
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              <Icon className="h-4 w-4" />
                            )}
                          </div>

                          <div className="min-w-0 flex-1">
                            <div
                              className={`text-sm font-semibold truncate ${
                                isActive ? "text-white" : "text-gray-900"
                              }`}
                            >
                              {item.title}
                            </div>
                            <div
                              className={`text-xs truncate ${
                                isActive ? "text-blue-100" : "text-gray-500"
                              }`}
                            >
                              {item.subtitle}
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-2 shrink-0">
                          {item.badge && (
                            <span
                              className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-semibold ${
                                isActive
                                  ? "bg-white/20 text-white"
                                  : "bg-gray-100 text-gray-700"
                              }`}
                            >
                              {item.badge}
                            </span>
                          )}

                          {isActive && (
                            <span className="flex items-center text-xs font-medium text-blue-100 gap-1 bg-white/10 px-1.5 py-0.5 rounded">
                              <CornerDownLeft className="h-3 w-3" />
                            </span>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}

            {/* Empty state when query has no results */}
            {hasQuery &&
              !isLoadingBaseData &&
              !isLoadingProducts &&
              !hasSearchResults && (
                <div className="rounded-xl border border-dashed border-gray-200 px-6 py-10 text-center">
                  <div className="text-base font-semibold text-gray-900">
                    Nessun risultato trovato per "{searchQuery}"
                  </div>
                  <div className="mt-1 text-xs text-gray-500">
                    Prova a cercare per codice ordine, nome cliente, email o SKU prodotto.
                  </div>
                </div>
              )}
          </div>

          {/* Footer Shortcuts Legend */}
          <div className="flex items-center justify-between border-t border-gray-100 px-4 py-2.5 bg-gray-50/80 text-xs text-gray-500">
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1">
                <kbd className="rounded border border-gray-200 bg-white px-1.5 py-0.5 text-[10px] font-medium shadow-2xs">
                  <ArrowUp className="h-2.5 w-2.5 inline" />
                  <ArrowDown className="h-2.5 w-2.5 inline" />
                </kbd>
                Naviga
              </span>
              <span className="flex items-center gap-1">
                <kbd className="rounded border border-gray-200 bg-white px-1.5 py-0.5 text-[10px] font-medium shadow-2xs">
                  ↵
                </kbd>
                Seleziona
              </span>
            </div>

            <span className="flex items-center gap-1">
              <kbd className="rounded border border-gray-200 bg-white px-1.5 py-0.5 text-[10px] font-medium shadow-2xs">
                ESC
              </kbd>
              Chiudi
            </span>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

